import { FC, useEffect } from 'react'
import { getDate, getDayOfWeek } from 'lib/date'
import { AvailableAppointmentTime } from 'graphql/types'
import useMap from 'hooks/useMap'
import Carousel, { TProps as TCarouselProps } from 'components/Carousel'
import Typography from 'components/Typography'
import Box from './components/Box'
import styles from './DatePicker.module.scss'

export type TDatePickerSelected = {
  dateString?: string
  date?: Date
  time?: string
}

export type TProps = {
  items: Array<AvailableAppointmentTime>
  onChange: (date: TDatePickerSelected) => void
  defaultValue?: TDatePickerSelected
  onDaySelect?: (date: string, weekday: string) => void
  onHourSelect?: (hour: string) => void
} & Pick<TCarouselProps, 'slidesPerPage' | 'slidesGap'>

const DatePicker: FC<TProps> = ({
  items,
  onChange,
  defaultValue,
  onDaySelect,
  onHourSelect,
  ...rest
}) => {
  const [selected, { set, setAll }] = useMap<TDatePickerSelected>({})

  const slots = selected?.dateString
    ? items.find(({ day }) => day === selected?.dateString)?.timeSlots
    : []

  const handleChange = (item: keyof TDatePickerSelected, value: string) => {
    if (item === 'dateString' && value !== selected.dateString) {
      set('time', '')
    }
    set(item, value)
  }

  useEffect(() => {
    if (selected.dateString) {
      const date = getDate(selected.dateString, selected?.time)
      set('date', date)
      onChange({ ...selected, date })
    }
  }, [selected.dateString, selected.time])

  useEffect(() => {
    setAll(defaultValue || {})
  }, [defaultValue])

  return (
    <div data-testid="ecDatePicker" className={styles.ecDayPicker}>
      <Typography bold>Selecione um dia</Typography>
      <Carousel
        slidesInfinite={false}
        items={items?.map(({ day }) => {
          const asDate = getDate(day as string)

          return (
            <Box
              key={day}
              title={getDayOfWeek(asDate, { day: '2-digit' })}
              description={getDayOfWeek(asDate, {
                weekday: 'short',
              }).toUpperCase()}
              onClick={() => {
                handleChange('dateString', day as string)
                if (onDaySelect)
                  onDaySelect(
                    day as string,
                    getDayOfWeek(asDate, { weekday: 'long' }),
                  )
              }}
              active={selected.dateString === day}
            />
          )
        })}
        navigation={{
          variant: 'outside',
        }}
        pagination={{
          hide: true,
        }}
        {...rest}
      />
      {slots && slots.length > 0 && (
        <div className={styles.ecTimePicker}>
          <Typography bold>Selecione um horário</Typography>
          <Carousel
            slidesInfinite={false}
            items={slots.map((time) => {
              const formattedTime = time ? `${time.split(':')[0]}h` : ''

              return (
                <Box
                  key={time}
                  description={formattedTime}
                  onClick={() => {
                    handleChange('time', time as string)
                    if (onHourSelect) onHourSelect(time as string)
                  }}
                  active={selected.time === time}
                />
              )
            })}
            navigation={{
              variant: 'outside',
            }}
            pagination={{
              hide: true,
            }}
            {...rest}
          />
        </div>
      )}
    </div>
  )
}

export default DatePicker
